import React, { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@remix-run/react";
import { faBars, faUser, faXmarkLarge } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from "@core/components/ui/drawer";
import { ClientOnly } from "@core/components/client-only";
import { MiniMenu } from "@site/components/MiniMenu/MiniMenu";
// import { HeaderTextSearchFilter } from "@site/components/HeaderTextSearchFilter";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { HeaderAccountKindPill } from "@/overrides/site/components/HeaderAccountKindPill";

type HeaderProps = PropsWithChildren & {
  logo?: React.ReactNode;
  menu?: React.ReactNode;
};

/**
 * Slots:
 *   - `logo`     → custom logo (defaults to cobrand + main logos)
 *   - `menu`     → right-side controls (cart / account / MiniMenu)
 *   - `children` → middle nav row (rendered inside `.header-nav-bar`)
 */
export const Header = ({ children, logo, menu }: HeaderProps) => {
  const { t } = useTranslation();

  return (
    <header className="site-header print:hidden" data-testid="site-header">
      <div className="site-header-inner">
        <div className="header-top">
          {/* Left: logo(s) */}
          {logo ? (
            <>{logo}</>
          ) : (
            <>
              <Link to="/" className="header-cobrand-link">
                <img
                  src="/assets/rd3-logo.png"
                  alt="RD3 Medical Group"
                  className="header-cobrand-logo"
                />
              </Link>
              <Link to="/" className="header-logo-link" data-testid="site-logo-link">
                <img src="/assets/logo.png" alt="New Solutions" className="header-logo" />
              </Link>
            </>
          )}
          {/* <ExtensionPoint name="_site.header.logo.after" /> */}

          {/* Right: menu (cart / account / etc.) */}
          <div className="header-right">
            <ExtensionPoint name="_site.header.beforeMiniMenu" />
            <ExtensionPoint name="_site.header.afterOrderingFor" />
            {/* Desktop menu */}
            <div className="hidden md:flex items-center gap-6">
              {menu ?? <MiniMenu />}
            </div>

            {/* Mobile user drawer */}
            <div className="flex items-center gap-2 md:hidden">
              <HeaderAccountKindPill />
              <ClientOnly>
                <Drawer direction="right">
                  <DrawerTrigger>
                    <span className="header-icon-btn p-3">
                      <FontAwesomeIcon icon={faUser} size="2xl" />
                    </span>
                  </DrawerTrigger>
                  <DrawerContent className="p-4 h-screen">
                    <DrawerHeader className="flex justify-between">
                      <DrawerTitle className="text-3xl font-bold">
                        {t("ui.menu", "Menu")}
                      </DrawerTitle>
                      <DrawerClose className="mini-menu-close">
                        <FontAwesomeIcon icon={faXmarkLarge} size="xl" />
                      </DrawerClose>
                    </DrawerHeader>
                    <div className="flex flex-col w-full my-4 overflow-y-auto flex-1 min-h-0">
                      {menu ?? <MiniMenu />}
                    </div>
                  </DrawerContent>
                </Drawer>
              </ClientOnly>
            </div>

            {/* Mobile nav drawer */}
            <div className="lg:hidden">
              <ClientOnly>
                <Drawer direction="right">
                  <DrawerTrigger>
                    <span className="header-icon-btn p-3">
                      <FontAwesomeIcon icon={faBars} size="2xl" />
                    </span>
                  </DrawerTrigger>
                  <DrawerContent className="p-4 h-screen overflow-auto">
                    <DrawerHeader className="flex justify-between">
                      <DrawerTitle className="text-3xl font-bold">
                        {t("ui.menu", "Menu")}
                      </DrawerTitle>
                      <DrawerClose>
                        <FontAwesomeIcon icon={faXmarkLarge} size="xl" />
                      </DrawerClose>
                    </DrawerHeader>
                    {/* <div className="my-4">
                      <HeaderTextSearchFilter />
                    </div> */}
                    {children}
                  </DrawerContent>
                </Drawer>
              </ClientOnly>
            </div>
          </div>
        </div>

        {/* Bottom: nav bar (only rendered when children are provided) */}
        {children && (
          <nav className="header-nav-bar hidden lg:flex">
            {children}
          </nav>
        )}
      </div>
    </header>
  );
};